import { motion, type Variants } from "framer-motion";
import {
  Globe,
  Search,
  Server,
  Wrench,
  Mail,
  Star,
  TrendingUp,
  MousePointer2,
  Check,
  Bell,
  Clock,
  ArrowUpRight,
} from "lucide-react";

/* ── animation variants ───────────────────────────── */

const laptopVariants: Variants = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const dashItemVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const cardPop: Variants = {
  hidden: { opacity: 0, scale: 0.92, x: -24 },
  visible: {
    opacity: 1,
    scale: 1,
    x: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

/* ── data ──────────────────────────────────────────── */

const serviceCards = [
  { icon: Globe, label: "Website", bg: "bg-primary", shadow: "shadow-primary/20" },
  { icon: Search, label: "SEO", bg: "bg-secondary", shadow: "shadow-secondary/20" },
  { icon: Server, label: "Hosting", bg: "bg-[hsl(205,75%,42%)]", shadow: "shadow-[hsl(205,75%,42%)]/20" },
  { icon: Wrench, label: "Onderhoud", bg: "bg-[hsl(30,80%,38%)]", shadow: "shadow-[hsl(30,80%,38%)]/20" },
];

/* ── SVG graph ────────────────────────────────────── */

const graphLine =
  "M 0 52 C 15 50, 30 48, 50 44 S 80 38, 110 34 S 145 26, 175 22 S 210 16, 240 12 S 270 7, 295 4";
const graphArea = graphLine + " L 295 60 L 0 60 Z";

/* ── sparkle dots ─────────────────────────────────── */

function Sparkles() {
  const dots = [
    { x: -40, y: 30, delay: 1.8, size: 3 },
    { x: -20, y: 100, delay: 2.2, size: 2 },
    { x: -55, y: 170, delay: 2.6, size: 2.5 },
    { x: 20, y: 210, delay: 3.0, size: 2 },
    { x: -30, y: 260, delay: 2.0, size: 3 },
    { x: -60, y: 130, delay: 2.8, size: 1.5 },
  ];
  return (
    <>
      {dots.map((d, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-secondary/30"
          style={{ width: d.size, height: d.size, left: d.x, top: d.y }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: [0, 0.7, 0], scale: [0, 1, 0] }}
          transition={{ duration: 3, delay: d.delay, repeat: Infinity, repeatDelay: 5 }}
        />
      ))}
    </>
  );
}

/* ── floating service cards ───────────────────────── */

function FloatingServiceCards() {
  return (
    <motion.div
      className="absolute -left-[50px] top-[8%] flex flex-col gap-2 z-10"
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.15, delayChildren: 1.2 } } }}
    >
      <Sparkles />

      {serviceCards.map((card, i) => (
        <motion.div
          key={card.label}
          variants={cardPop}
          whileHover={i === 1 ? { scale: 1.08, y: -2, boxShadow: "0 12px 32px -8px hsl(var(--secondary) / 0.3)" } : { scale: 1.04 }}
          className={`
            flex items-center gap-2 pl-3 pr-5 py-2 rounded-lg
            ${card.bg} text-white
            shadow-md ${card.shadow}
            cursor-default relative
          `}
          style={{ minWidth: 110 }}
        >
          <card.icon className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="text-xs font-semibold tracking-wide">{card.label}</span>

          {/* Small decorative diamond on Hosting card */}
          {i === 2 && (
            <motion.div
              className="absolute -right-2 top-1/2 -translate-y-1/2 w-3 h-3 bg-background/80 rotate-45 rounded-sm shadow-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.2 }}
            />
          )}

          {/* X mark on SEO card (like in reference) */}
          {i === 1 && (
            <motion.div
              className="absolute -right-2 -top-1 w-4 h-4 bg-muted rounded-full flex items-center justify-center text-[8px] text-muted-foreground shadow-sm"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 2.4 }}
            >
              ×
            </motion.div>
          )}
        </motion.div>
      ))}

      {/* Cursor hovering near SEO card */}
      <motion.div
        className="absolute z-20 pointer-events-none"
        style={{ left: 100, top: 80 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0, 1, 1], x: [0, 0, 10, 10], y: [0, 0, 5, 5] }}
        transition={{ duration: 2, delay: 2.6, ease: "easeInOut" }}
      >
        <motion.div
          animate={{ y: [0, -1.5, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        >
          <MousePointer2 className="h-5 w-5 text-foreground/80 fill-background drop-shadow-md" />
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

/* ── dashboard screen content ─────────────────────── */

function DashboardContent() {
  return (
    <motion.div
      className="bg-muted/15 p-3 space-y-2"
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1, delayChildren: 0.5 } } }}
    >
      {/* Notification bar */}
      <motion.div variants={dashItemVariants} className="flex items-center gap-2 rounded-lg bg-primary/5 border border-primary/10 px-3 py-1.5">
        <Bell className="w-3 h-3 text-primary" />
        <span className="text-[9px] text-background/90 font-medium">3 nieuwe aanvragen deze week</span>
        <ArrowUpRight className="w-2.5 h-2.5 text-primary ml-auto" />
      </motion.div>

      {/* Big stat + graph */}
      <motion.div variants={dashItemVariants} className="rounded-xl bg-background border border-border/30 p-3 shadow-sm">
        <div className="flex items-baseline justify-between mb-0.5">
          <span className="text-xl font-extrabold text-foreground">+120%</span>
          <span className="text-[8px] text-muted-foreground">afgelopen 6 mnd</span>
        </div>
        <div className="text-[10px] text-muted-foreground mb-2">bezoekers</div>
        <svg viewBox="0 0 295 60" className="w-full h-[42px]" preserveAspectRatio="none">
          <motion.path
            d={graphArea}
            fill="hsl(var(--secondary) / 0.06)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
          />
          <motion.path
            d={graphLine}
            fill="none"
            stroke="hsl(var(--secondary))"
            strokeWidth="2"
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1.6, delay: 0.9, ease: "easeInOut" }}
          />
          <motion.circle
            cx="295" cy="4" r="3.5"
            fill="hsl(var(--secondary))"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 2.5 }}
          />
          <motion.circle
            cx="295" cy="4" r="3.5"
            fill="none" stroke="hsl(var(--secondary))" strokeWidth="1.5"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.4, 0], scale: [1, 2.5, 3] }}
            transition={{ duration: 2.2, delay: 4, repeat: Infinity, repeatDelay: 7 }}
          />
        </svg>
      </motion.div>

      {/* Three stat cards */}
      <div className="grid grid-cols-3 gap-1.5">
        <motion.div variants={dashItemVariants} className="rounded-lg bg-background border border-border/30 p-2 shadow-sm">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Mail className="w-3.5 h-3.5 text-success" />
            <span className="text-sm font-extrabold text-foreground">12</span>
          </div>
          <div className="text-[8px] text-muted-foreground">nieuwe leads</div>
        </motion.div>

        <motion.div variants={dashItemVariants} className="rounded-lg bg-background border border-border/30 p-2 shadow-sm">
          <div className="flex items-center gap-1 mb-0.5">
            <Star className="w-3 h-3 text-accent fill-accent" />
            <span className="text-sm font-extrabold text-foreground">4.9</span>
            <div className="flex gap-0.5 ml-0.5">
              {[...Array(3)].map((_, i) => (
                <Star key={i} className="w-2 h-2 text-accent fill-accent" />
              ))}
            </div>
          </div>
          <div className="text-[8px] text-muted-foreground">Google reviews</div>
        </motion.div>

        <motion.div variants={dashItemVariants} className="rounded-lg bg-background border border-border/30 p-2 shadow-sm">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-sm font-extrabold text-foreground">SEO</span>
            <TrendingUp className="w-3.5 h-3.5 text-success" />
          </div>
          <div className="text-[8px] text-muted-foreground">positie #3</div>
        </motion.div>
      </div>

      {/* Progress / tasks section */}
      <motion.div variants={dashItemVariants} className="rounded-xl bg-background border border-border/30 p-3 shadow-sm space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold text-foreground/80">Lopende taken</span>
          <span className="text-[8px] text-muted-foreground">3/5 afgerond</span>
        </div>
        {/* Progress bar */}
        <div className="w-full h-1.5 bg-muted/40 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-primary rounded-full"
            initial={{ width: 0 }}
            animate={{ width: "60%" }}
            transition={{ duration: 1.2, delay: 2, ease: "easeOut" }}
          />
        </div>
        {/* Task items */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Check className="w-3 h-3 text-success" />
            <span className="text-[9px] text-muted-foreground line-through">Website live gezet</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-3 h-3 text-success" />
            <span className="text-[9px] text-muted-foreground line-through">SSL certificaat actief</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-secondary" />
            <span className="text-[9px] text-foreground/70 font-medium">SEO optimalisatie</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── main component ───────────────────────────────── */

export function HeroDashboardMockup() {
  return (
    <div className="relative w-full max-w-[560px] ml-auto">
      {/* Soft glow */}
      <div className="absolute inset-0 -inset-x-12 -inset-y-12 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[75%] h-[65%] bg-secondary/[0.04] rounded-full blur-3xl" />
      </div>

      {/* Floating service cards */}
      <FloatingServiceCards />

      {/* Laptop */}
      <motion.div
        variants={laptopVariants}
        initial="hidden"
        animate="visible"
        className="relative z-[1]"
      >
        {/* Screen bezel */}
        <div className="bg-foreground rounded-t-2xl shadow-2xl overflow-hidden border border-border/10 p-[3px]">
          {/* Browser chrome */}
          <div className="bg-foreground rounded-t-xl px-3 py-2 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-destructive/70" />
              <div className="w-2.5 h-2.5 rounded-full bg-accent/70" />
              <div className="w-2.5 h-2.5 rounded-full bg-success/70" />
            </div>
            <div className="flex-1 mx-3">
              <div className="bg-muted/15 rounded-md px-3 py-1 text-[10px] text-background/60 flex items-center gap-1.5">
                <Search className="w-2.5 h-2.5" />
                <span>dashboard.onlinesalespecialist.nl</span>
              </div>
            </div>
            <div className="flex gap-1">
              <div className="w-5 h-4 rounded bg-secondary/80" />
            </div>
          </div>

          {/* Dashboard */}
          <DashboardContent />
        </div>

        {/* Laptop base */}
        <div className="relative mx-auto w-[55%] h-3.5 bg-foreground/90 rounded-b-xl shadow-lg">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-14 h-1 bg-muted/15 rounded-b" />
        </div>
        <div className="mx-auto w-[75%] h-[3px] bg-foreground/50 rounded-b-xl" />
      </motion.div>

      {/* Label below */}
      <motion.div
        className="flex justify-center mt-4"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.8, duration: 0.6 }}
      >
        <div className="bg-background border border-border/40 rounded-xl px-5 py-2 shadow-sm">
          <span className="text-xs font-semibold text-foreground/70 tracking-wide">
            Online Sale Specialist
          </span>
        </div>
      </motion.div>
    </div>
  );
}
